import React, { useEffect, useState } from 'react';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { Icon, IconButton, ListItemIcon, Menu, MenuItem, Tooltip } from '@mui/material';

import ProgressBar from '../controls/ProgressBar';
import ErrorMessage from '../controls/ErrorMessage';
import DialogDelete from '../controls/dialogs/DialogDelete';
import DialogConfirm from '../controls/dialogs/DialogConfirm';
import DialogPublicLayer from '../controls/dialogs/DialogPublicLayer';
import DialogAssetLayer from '../controls/dialogs/DialogAssetLayer';
import DialogShareAssets from '../controls/dialogs/DialogShareAssets';

import { LayerPublic } from '../../models/maps/layerPublic';
import { LayerType } from '../../models/types/layerType';
import { LayerViewModel } from '../../models/maps/layerViewModel';
import { organisatieService } from '../../services/organisaties.service';
import { planManagementService } from '../../services/plan-management.service';
import { authService } from '../../services/auth.service';


const LayersActionMenu = ({ 
  params,
  layerType,
  onRefresh
}: {
  params: GridRenderCellParams<LayerViewModel>;
  layerType: LayerType;
  onRefresh: () => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [open, setOpenDialogs] = useState({
    edit: false,
    delete: false,
    active: false,
    shareAdd: false,
    shareAsPlan: false,
  });

  useEffect(() => {
    if (params?.value?.id) {
      setSelectedLayerId(params?.value?.id);
    }
  }, [params?.value?.id]);

  // Handlers to open/close each dialog
  const openDialog = (dialog: keyof typeof open) => {
    // setSelectedId(id);
    setOpenDialogs((prev) => ({ ...prev, [dialog]: true }));
    handleCloseMenu();
  };

  const closeDialog = (dialog: keyof typeof open) => {
    setOpenDialogs((prev) => ({ ...prev, [dialog]: false }));
    setSelectedLayerId(null);
  };
  
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setSelectedLayerId(id) // id: string
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleToggleActive = async () => {
    handleCloseMenu();
    if (!selectedLayerId) return;

    try {
      setLoading(true);
  
      const layer = await organisatieService.getPublicLayer(selectedLayerId);
      if (!layer) {
        throw new Error("Layer niet gevonden");
      }

      const updatedLayer: LayerPublic = {
        ...layer,
        active: !layer.active,
      };
      const response = await organisatieService.updatePublicMap(updatedLayer);
      if (response?.ok) {
        closeDialog('active');
        onRefresh();
        closeDialog('active');
      }
    } 
    catch (error) {
      console.error("Error toggling active:", error);
      setError("Er is een fout opgetreden bij het wijzigen van de status");
    } 
    finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      let response: any;
      if (selectedLayerId) {
        if (layerType === LayerType.PUBLIC) {
          response = await organisatieService.deletePublicMap(selectedLayerId);
        }
        else {
          response = await planManagementService.deleteAssetLayer(selectedLayerId);
        }
      } 

      if (response.ok) {
        setLoading(false);
        onRefresh();
      }
    }
    catch (error) {
      setLoading(false);
      console.error('Error deleting organisatie:', error);
      setError('Er is een fout opgetreden');
    }
  }
  
  const handleSubmit = async (layer: LayerViewModel) => {
    try {
      setLoading(true);

      const layerPublic: LayerPublic = {
        id: layer.id,
        url: layer.url ?? '',
        sourceName: layer.sourceName ?? '',
        layerDisplayName: layer.displayName ?? '',
        layerName: layer.layerName ?? '',
        description: layer.description,
        metadataUrl: layer.metadataUrl,
        type: layer.type as 'wms' | 'wfs' | 'geopackage' | 'ogc_features' | 'ogc_tiles',
        active: layer.active ?? true,
        lastModified: layer.lastModified,
      };

      const response = await organisatieService.updatePublicMap(layerPublic);

      if (response?.ok) {
        onRefresh();
        closeDialog('edit');
      }
    }
    catch (error) {
      console.error('Error updating public layer:', error);
      setError('Er is een fout opgetreden');
    }
    finally {
      setLoading(false);
    }
  }

  const handleSubmitAssetLayer = async (layer: LayerViewModel) => {
    if (!selectedLayerId) return;

    try {
      setLoading(true);
      const details = {
        displayName: layer.displayName ?? '',
        description: layer.description,
        metadataUrl: layer.metadataUrl,
      };
      const response = await planManagementService.updateAssetLayerDetails(selectedLayerId, details);

      if (response?.ok) {
        onRefresh();
        closeDialog('edit');
      }
    }
    catch (error) {
      console.error('Error updating asset layer:', error);
      setError('Er is een fout opgetreden');
    }
    finally {
      setLoading(false);
    }
  }

  const handleToggleUsePlanShareSettings = async () => {
    if (!selectedLayerId) return;

    try {
      setLoading(true);
      const usePlanShareSettings = Boolean(params.row.usePlanShareSettings);

      const response = usePlanShareSettings
        ? await planManagementService.disableAssetLayerUsePlanShareSettings(selectedLayerId)
        : await planManagementService.enableAssetLayerUsePlanShareSettings(selectedLayerId);

      if (response?.ok) {
        onRefresh();
        closeDialog('shareAsPlan');
      }
    }
    catch (error) {
      console.error('Error toggling asset layer use plan share settings:', error);
      setError('Er is een fout opgetreden');
    }
    finally {
      setLoading(false);
    }
  };

  return (
    <>
      {authService.isKeyUser() && (
        <IconButton onClick={(e) => handleOpenMenu(e, params.id as string)}>
          <Tooltip title="Acties" placement="right" arrow>
            <Icon>more_vert</Icon>
          </Tooltip>
        </IconButton>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {authService.isKeyUser() && (
          <>
            <MenuItem onClick={() => openDialog('edit')}>
              <ListItemIcon>
                <Icon>edit</Icon>
              </ListItemIcon>
              Wijzigen
            </MenuItem>
            <MenuItem onClick={() => openDialog('delete')}>
              <ListItemIcon>
                <Icon>delete</Icon>
              </ListItemIcon>
              Verwijderen
            </MenuItem>
            {layerType === LayerType.ASSET && (
              <MenuItem onClick={() => openDialog('shareAdd')}>
                <ListItemIcon>
                  <Icon>share</Icon>
                </ListItemIcon>
                {params.row.usePlanShareSettings ? 'Organisaties bekijken' : 'Delen met organisaties beheren'}
              </MenuItem>
            )}
            {layerType === LayerType.ASSET && (
              <MenuItem onClick={() => openDialog('shareAsPlan')}>
                <ListItemIcon>
                  <Icon color={params.row.usePlanShareSettings ? 'disabled' : 'success'}>
                    {params.row.usePlanShareSettings ? 'toggle_off' : 'toggle_on'}
                  </Icon>
                </ListItemIcon>
                {params.row.usePlanShareSettings ? 'Plan deelinstellingen uit' : 'Plan deelinstellingen aan'}
              </MenuItem>
            )}
            {layerType === LayerType.PUBLIC && (
              <MenuItem onClick={() => openDialog('active')}>
                <ListItemIcon>
                  <Icon color={params.row.active ? 'disabled' : 'success'}>
                    {params.row.active ? "toggle_off" : "toggle_on"}
                  </Icon>
                </ListItemIcon>
                {params.row.active ? "Deactiveren" : "Activeren"}
              </MenuItem>
            )}
          </>
        )}
      </Menu>

      <DialogDelete
        open={open.delete}
        name={params.row.displayName || params.row.layerName}
        onClose={() => closeDialog('delete')}
        onDelete={handleConfirmDelete}
       />

      {layerType === LayerType.PUBLIC && (
        <DialogConfirm
          open={open.active}
          title={params.row.active ? 'Kaartlaag deactiveren' : 'Kaartlaag activeren'}
          contentText={params.row.active
            ? 'Weet je zeker dat je deze kaartlaag wilt deactiveren?'
            : 'Weet je zeker dat je deze kaartlaag wilt activeren?'}
          confirmButtonText={'Ja dat weet ik zeker'}
          onClose={() => { closeDialog('active') }}
          onConfirm={handleToggleActive}
        />
      )}

      {layerType === LayerType.PUBLIC && (
        <DialogPublicLayer
          open={open.edit}
          layer={params.row}
          onClose={() => { closeDialog('edit') }}
          onSubmit={handleSubmit}
        />
      )}

      {layerType === LayerType.ASSET && (
        <DialogAssetLayer
          open={open.edit}
          layer={params.row}
          onClose={() => { closeDialog('edit') }}
          onSubmit={handleSubmitAssetLayer}
        />
      )}

      {layerType === LayerType.ASSET && (
        <DialogShareAssets
          open={open.shareAdd}
          assetLayerId={selectedLayerId ?? undefined}
          allowMultiple={true}
          groupBySource={true}
          onClose={() => { closeDialog('shareAdd') }}
          onSubmit={onRefresh}
        />
      )}

      {layerType === LayerType.ASSET && (
        <DialogConfirm
          open={open.shareAsPlan}
          title={params.row.usePlanShareSettings ? 'Gebruik eigen deelinstellingen' : 'Gebruik plan deelinstellingen'}
          contentText={params.row.usePlanShareSettings
            ? `Weet je zeker dat je de huidige plan deelinstellingen wilt uitschakelen voor deze kaartlaag? 
                Let op! Hierdoor wijzigen de deelinstellingen van deze kaartlaag niet meer automatisch mee maar kun je wel specifiek de deelinstellingen van deze kaartlaag aanpassen.`
            : `Weet je zeker dat je de deelinstellingen van plannen wilt gebruiken voor deze kaartlaag? 
                Let op! Hierdoor worden de deelinstellingen van deze kaartlaag gelijk aan die van het plan en worden specifieke deel instellingen overschreven.`}
          confirmButtonText={'Ja dat weet ik zeker'}
          onClose={() => { closeDialog('shareAsPlan') }}
          onConfirm={handleToggleUsePlanShareSettings}
        />
      )}

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}
    </>
  );
};

export default LayersActionMenu;
